import { PeopleAlt } from '@mui/icons-material'
import { Box, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateAppFeatureCache, usePatchAdminAppFeature, useGetAdminAppFeatures } from 'src/api/app-features'
import { isGlobalOnlyAppFeature } from 'src/constants/appFeatureKeys'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { AddCustomersToFeatureDialog } from 'src/screens/admin/feature-flags/AddCustomersToFeatureDialog'
import { CenteredSpinner } from 'src/components/Spinner'
import { useDialogCtx } from 'src/hooks/context-hooks'

const AdminFeatureFlagsScreen = () => {
  const { t } = useTranslation(['feature-flags', 'common'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { data: rows, isLoading, isFetching, isError, error, refetch } = useGetAdminAppFeatures()
  const { mutate: patchFeature, isLoading: isPatching } = usePatchAdminAppFeature()

  useEffect(() => {
    if (isError) {
      const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? t('load-failed')
      toast.error(message)
    }
  }, [error, isError, t])

  const openCustomersDialog = useCallback(
    (featureId: number, displayName: string) => {
      void showDialog(AddCustomersToFeatureDialog, {
        opts: { maxWidth: 'lg', fullWidth: true },
        componentProps: { featureId, featureName: displayName },
      })
    },
    [showDialog],
  )

  const applyToggle = useCallback(
    (id: number, isEnabled: boolean, pop: () => void) => {
      patchFeature(
        { id, body: { isEnabled: !isEnabled } },
        {
          onSuccess: () => {
            invalidateAppFeatureCache.getAdminAppFeatures()
            invalidateAppFeatureCache.getAppFeatureMap()
            toast.success(t('saved'))
            pop()
          },
          onError: () => {
            toast.error(t('save-failed'))
          },
        },
      )
    },
    [patchFeature, t],
  )

  const handleToggle = useCallback(
    (id: number, isEnabled: boolean, displayName: string) => {
      const nextEnabled = !isEnabled
      showConfirmDialog(
        t('toggle-confirm-title'),
        t('toggle-confirm-body', { name: displayName, action: nextEnabled ? t('toggle-enable') : t('toggle-disable') }),
        {
          acceptText: nextEnabled ? t('toggle-enable') : t('toggle-disable'),
          cancelText: String(t('cancel', { ns: 'common' })),
        },
        pop => applyToggle(id, isEnabled, pop),
      )
    },
    [applyToggle, showConfirmDialog, t],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'key',
        headerName: t('col-key'),
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }) => (
          <Typography variant="body2" fontFamily="monospace">
            {row.key}
          </Typography>
        ),
      },
      {
        field: 'displayName',
        headerName: t('col-name'),
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'isEnabled',
        headerName: t('col-status'),
        width: 120,
        valueGetter: ({ row }) => (row.isEnabled ? t('status-active') : t('status-disabled')),
      },
      {
        field: 'assignedCustomerCount',
        headerName: t('col-customers'),
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) =>
          isGlobalOnlyAppFeature(row.key) ? (
            <Typography variant="body2" color="text.secondary">
              {t('global-only-feature')}
            </Typography>
          ) : (
            row.assignedCustomerCount
          ),
      },
      {
        field: 'actions',
        headerName: t('col-actions'),
        width: 140,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title={String(row.isEnabled ? t('toggle-disable') : t('toggle-enable'))}>
              <Switch
                checked={row.isEnabled}
                disabled={isPatching}
                onChange={() => handleToggle(row.id, row.isEnabled, row.displayName)}
                inputProps={{ 'aria-label': row.key }}
                size="small"
              />
            </Tooltip>
            {!isGlobalOnlyAppFeature(row.key) && (
              <Tooltip title={String(t('add-customers'))}>
                <IconButton
                  color="primary"
                  size="medium"
                  disabled={!row.isEnabled}
                  onClick={() => openCustomersDialog(row.id, row.displayName)}
                  aria-label={String(t('add-customers'))}
                >
                  <PeopleAlt />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [handleToggle, isPatching, openCustomersDialog, t],
  )

  if (isLoading) {
    return <CenteredSpinner />
  }

  return (
    <CommonPageLayout
      titleSection={{
        title: t('title'),
        subtitle: t('subtitle'),
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('help')}
        </Typography>
        <GenericTable
          id="admin-app-feature-flags"
          enableToolbar={false}
          data={rows ?? []}
          columns={columns}
          loading={isFetching || isPatching}
          getRowHeight={() => 48}
          autoHeight
          noRowsMessage={isError ? t('load-failed') : undefined}
        />
        {isError && (
          <GridActionButton
            buttonText={String(t('retry', { ns: 'common', defaultValue: 'Retry' }))}
            variant="outlined"
            width="120px"
            height="42px"
            onClick={() => void refetch()}
          />
        )}
      </Stack>
    </CommonPageLayout>
  )
}

export default AdminFeatureFlagsScreen
